import { ChangeDetectionStrategy, Component } from '@angular/core';

/**
 * `<nb-table>` — the design-system table shell (Mockup web.css `.tablecard` + `.tbl`).
 *
 * Presentational only. The consumer projects a real `<table>` (with its own
 * `<thead>`/`<tbody>`) so the semantics, scope and keyboard behaviour come from
 * native HTML and screen readers expose proper row/column structure. This
 * component supplies the surrounding `.tablecard` card (rounded, hairline
 * border, clipped corners) and styles the descendant table elements: uppercase
 * `--surface-2` header cells, hairline `--border` rows, and a row hover tint.
 *
 * Use the `.nm` (name / emphasised) and `.mut` (muted secondary) helper classes
 * on cell content exactly as in the mockup. An optional `<nb-table-toolbar>` may
 * be projected as the first child to render the `.toolbar` strip above the table.
 *
 * @example
 * <nb-table>
 *   <nb-table-toolbar>
 *     <nb-button variant="sec" size="sm"><nb-icon [icon]="Filter"/> Filtrer</nb-button>
 *   </nb-table-toolbar>
 *   <table>
 *     <thead><tr><th scope="col">Navn</th><th scope="col">Status</th></tr></thead>
 *     <tbody>
 *       <tr><td class="nm">Kari Nordmann</td><td class="mut">Aktiv</td></tr>
 *     </tbody>
 *   </table>
 * </nb-table>
 */
@Component({
  selector: 'nb-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<ng-content select="nb-table-toolbar" />
    <ng-content />`,
  styleUrl: './table.css',
})
export class NbTable {}

/**
 * `<nb-table-toolbar>` — the action strip above an `<nb-table>` (Mockup `.toolbar`).
 *
 * A horizontal flex row (gap 10px, padding 14px 18px) closed by a hairline
 * bottom border, used to host filter chips, search fields or action buttons.
 * Project it as the first child of `<nb-table>`.
 */
@Component({
  selector: 'nb-table-toolbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'toolbar',
  },
  template: `<ng-content />`,
  styleUrl: './table-toolbar.css',
})
export class NbTableToolbar {}
